import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Banknote, CreditCard, Landmark, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, apiError } from "@/lib/api";
import { formatIsoDay, formatTRY } from "@/lib/format";
import { ACCOUNT_TYPES, AccDot, AccountBadge } from "@/lib/finance";
import { EmptyRow, LoadingRow, PageHeader, Pager } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import CashflowPanel from "@/components/finance/CashflowPanel";
import SupplierPaymentsPanel from "@/components/finance/SupplierPaymentsPanel";
import ExpenseDialog from "@/components/finance/ExpenseDialog";
import AccountsAdmin from "@/components/finance/AccountsAdmin";
import { DocLink } from "@/components/common/DocLink";

const ICON = { kasa: Banknote, banka: Landmark, pos: CreditCard, kredi_karti: CreditCard };

const AccountCard = ({ a, active, onClick }) => {
  const Icon = ICON[a.type];
  return (
    <button type="button" onClick={onClick} data-testid={`account-card-${a.code}`}
      style={a.color ? { borderLeft: `4px solid ${a.color}` } : undefined} className={`panel p-4 text-left transition-[border-color,box-shadow] ${active ? "ring-2 ring-blue-600 border-blue-600" : "hover:border-blue-400"}`}>
      <div className="flex items-center justify-between"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{ACCOUNT_TYPES[a.type]}</span><Icon className="h-4 w-4 text-slate-400" /></div>
      <div className="text-[13px] font-medium text-slate-800 mt-1 truncate flex items-center gap-1.5"><AccDot color={a.color} testid={`account-card-dot-${a.code}`} />{a.name}</div>
      <div className={`font-mono text-xl font-semibold mt-1 ${a.balance < 0 ? "text-rose-600" : "text-slate-900"}`} data-testid={`account-balance-${a.code}`}>{a.type === "kredi_karti" ? `Borç ${formatTRY(-a.balance)}` : formatTRY(a.balance)}</div>
      {a.iban && <div className="text-[10px] font-mono text-slate-400 mt-1 truncate">{a.iban}</div>}
    </button>
  );
};

function MovementsTable({ accountId, status, reload, onChanged }) {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  useEffect(() => { setPage(1); }, [accountId, status]);
  useEffect(() => { setRes(null); api.get("/finans/movements", { params: { account_id: accountId, status, page, page_size: 30 } }).then(({ data }) => setRes(data)); }, [accountId, status, page, reload]);
  const act = async (m, kind) => {
    try { await api.post(`/finans/movements/${m.id}/${kind}`); toast.success(kind === "realize" ? "Ödeme gerçekleşti, bakiye güncellendi" : "Planlı ödeme iptal edildi"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const planned = status === "planlandi";
  return (
    <div className="panel overflow-hidden">
      <div className="overflow-x-auto max-h-[calc(100vh-420px)]">
        <table className="erp-table" data-testid={planned ? "planned-table" : "movements-table"}>
          <thead><tr><th>Tarih</th><th>Hesap</th><th>Tür</th><th>Kategori</th><th>Açıklama</th><th className="text-right">Giriş</th><th className="text-right">Çıkış</th><th>Kullanıcı</th>{planned && <th className="text-right">İşlem</th>}</tr></thead>
          <tbody>
            {!res ? <LoadingRow cols={9} /> : res.items.length === 0 ? <EmptyRow cols={9} text="Hareket yok" /> : res.items.map((m) => (
              <tr key={m.id} className={m.status === "iptal" ? "opacity-50 line-through" : ""} data-testid={`movement-row-${m.id}`}>
                <td className="font-mono text-xs">{formatIsoDay(m.date)}</td>
                <td className="text-xs max-w-[180px]"><AccountBadge name={m.account_name} color={m.account_color} /></td>
                <td className="text-xs"><DocLink link={m.link} testid={`movement-doc-link-${m.id}`}>{m.type_label}</DocLink></td>
                <td className="text-xs text-slate-500">{m.category}</td>
                <td className="max-w-[300px] truncate" title={m.description}>{m.reconciled_at && <span className="mr-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" title={`Mutabık · ${String(m.reconciled_at).slice(0, 10)}`} data-testid={`movement-reconciled-${m.id}`}>Mutabık</span>}{m.instrument_no && <Link to={`/cek-senet?id=${m.instrument_id}`} className="mr-1 rounded bg-amber-50 px-1.5 py-0.5 font-mono text-[11px] text-amber-800 hover:underline" data-testid={`movement-instrument-${m.id}`}>{m.instrument_no}</Link>}{m.description}</td>
                <td className="text-right font-mono text-emerald-700">{m.direction === "giris" ? formatTRY(m.amount) : ""}</td>
                <td className="text-right font-mono text-rose-600">{m.direction === "cikis" ? formatTRY(m.amount) : ""}</td>
                <td className="text-xs">{m.created_by_name}</td>
                {planned && (
                  <td className="text-right">{can("finans", "edit") && m.status === "planlandi" && (
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => act(m, "realize")} data-testid={`planned-realize-${m.id}`}>Öde</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-600" onClick={() => act(m, "cancel")} data-testid={`planned-cancel-${m.id}`}>İptal</Button>
                    </div>)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {res && <Pager page={page} pageSize={30} total={res.total} onPage={setPage} testid={planned ? "planned-pager" : "movements-pager"} />}
    </div>
  );
}

export default function Finance() {
  const { can } = useAuth();
  const [acc, setAcc] = useState(null);
  const [selected, setSelected] = useState("");
  const [reload, setReload] = useState(0);
  const [adding, setAdding] = useState(false);
  useEffect(() => { api.get("/finans/accounts").then(({ data }) => setAcc(data)); }, [reload]);
  const bump = () => setReload((r) => r + 1);
  return (
    <div data-testid="finance-page">
      <PageHeader title="Finans" subtitle="Kasa, banka ve POS bakiyeleri · nakit akış projeksiyonu" testid="finance-title">
        {can("finans", "create") && acc && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setAdding(true)} data-testid="new-expense-button"><Plus className="h-4 w-4 mr-1" />Gider / Ödeme Gir</Button>}
      </PageHeader>
      {!acc ? <div className="panel h-28 animate-pulse mb-4" /> : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <div className="panel p-4 bg-slate-900 border-slate-900 text-white" data-testid="finance-total">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Toplam Nakit Varlık</div>
            <div className="font-mono text-2xl font-semibold mt-2" data-testid="finance-total-value">{formatTRY(acc.total)}</div>
            <div className="text-[11px] text-slate-400 mt-1">Kasa {formatTRY(acc.by_type.kasa)} · Banka {formatTRY(acc.by_type.banka)}</div>
          </div>
          {acc.card_debt > 0 && <div className="panel p-4 border-rose-200 bg-rose-50" data-testid="finance-card-debt">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Kredi Kartı Borcu (yükümlülük)</div>
            <div className="font-mono text-2xl font-semibold mt-2 text-rose-700" data-testid="finance-card-debt-value">{formatTRY(acc.card_debt)}</div>
            <div className="text-[11px] text-rose-600 mt-1">Nakit varlığa dahil değildir</div>
          </div>}
          {acc.items.length === 0 && <div className="panel p-4 col-span-2 lg:col-span-4 text-[13px] text-slate-600" data-testid="finance-no-accounts">Henüz kasa/banka hesabı yok. "Hesap Tanımları" sekmesinden hesap ve açılış bakiyelerini girin.</div>}
          {acc.items.map((a) => <AccountCard key={a.id} a={a} active={selected === a.id} onClick={() => setSelected(selected === a.id ? "" : a.id)} />)}
        </div>
      )}
      <Tabs defaultValue="akis">
        <TabsList className="mb-3">
          <TabsTrigger value="akis" data-testid="finance-tab-cashflow">Nakit Akışı</TabsTrigger>
          <TabsTrigger value="hareketler" data-testid="finance-tab-movements">Hareketler</TabsTrigger>
          <TabsTrigger value="planli" data-testid="finance-tab-planned">Planlı Ödemeler</TabsTrigger>
          <TabsTrigger value="tedarikci" data-testid="finance-tab-supplier">Tedarikçi Ödemeleri</TabsTrigger>
          {can("finans", "create") && <TabsTrigger value="hesaplar" data-testid="finance-tab-accounts">Hesap Tanımları</TabsTrigger>}
        </TabsList>
        <TabsContent value="hesaplar"><AccountsAdmin onChanged={bump} /></TabsContent>
        <TabsContent value="akis"><CashflowPanel reload={reload} /></TabsContent>
        <TabsContent value="tedarikci"><SupplierPaymentsPanel reload={reload} onChanged={bump} /></TabsContent>
        <TabsContent value="hareketler">
          {selected && <div className="text-[12px] text-slate-500 mb-2">Filtre: {acc?.items.find((a) => a.id === selected)?.name} <button className="text-blue-600 ml-1" onClick={() => setSelected("")} data-testid="movements-clear-filter">Temizle</button></div>}
          <MovementsTable accountId={selected} status="gerceklesti" reload={reload} onChanged={bump} />
        </TabsContent>
        <TabsContent value="planli"><MovementsTable accountId={selected} status="planlandi" reload={reload} onChanged={bump} /></TabsContent>
      </Tabs>
      {adding && <ExpenseDialog accounts={acc.items} onClose={() => setAdding(false)} onDone={() => { setAdding(false); bump(); }} />}
    </div>
  );
}
